import { PaginationLookupResult } from './pagination-lookup-result';
import { AbstractListLoader } from './abstract-list-loader';
import { Observable, Subscription } from 'rxjs';
import { ColumnSortModel, TableSortModel } from '../../tablesort/tableSortModel';
import { LoadingState } from './loading-state';

export class ListLoaderPagination<T> extends AbstractListLoader<T> {
  private lookupItemFunc: (offset: number, limit: number, sortColumn: ColumnSortModel<T>) => Observable<PaginationLookupResult<T>>;
  private columnSortModel: ColumnSortModel<T>;

  pageSubscriptions: Subscription;

  constructor(parameter: ListLoaderPaginationBuilder<T>) {
    super(parameter.pageSize);
    this.currentPage = parameter.currentPage;
    this.lookupItemFunc = parameter.lookupItemFunc;
    this.throwError = parameter.isThrowError();
    this.totalItemsVisible = parameter.totalItems;
    if (parameter.noSortModel) {
      this.internalChangePage(this.currentPage);
    }
  }

  applySortModel(tableSortModel: TableSortModel<T>) {
    this.tableSortModel = tableSortModel;
    const sub = this.tableSortModel.sortedColumns$.subscribe((colModel) => {
      this.columnSortModel = colModel;
      if (this.columnSortModel) {
        this.internalChangePage(this.currentPage);
      }
    });
    this.subscriptions.push(sub);
  }

  get resumable(): boolean {
    return false;
  }

  get stoppable(): boolean {
    return false;
  }

  protected internalChangePage(page: number) {
    if (this.stateSubject.value === LoadingState.INIT) {
      this.stateSubject.next(LoadingState.INITIAL_LOADING);
    } else {
      this.stateSubject.next(LoadingState.PAGE_LOADING);
    }
    this.currentPage = page;

    this.pageSubscriptions?.unsubscribe();
    this.pageSubscriptions = this.lookupItemFunc((this.currentPage - 1) * this._pageSize, this._pageSize, this.columnSortModel).subscribe({
      next: (r) => {
        this.itemsSubject.next(r.result);
        this.totalItemsVisible = r.totalItems;
        this.stateSubject.next(LoadingState.ALL_DATA_LOADED);
      },
      error: (err) => this.filterAccessDenied(err)
    });

    this.subscriptions.push(this.pageSubscriptions);
  }

  remove(item: T): void {
    this.internalChangePage(this.currentPage);
  }
}

export class ListLoaderPaginationBuilder<T> {
  private _lookupItemFunc: (offset: number, limit: number, sortColumn: ColumnSortModel<T>) => Observable<PaginationLookupResult<T>>;
  private _pageSize: number = undefined;
  private _currentPage: number = undefined;
  private _noSortModel = false;
  private throwError = true;
  private _totalItems: number = undefined;

  static builder<U>(): ListLoaderPaginationBuilder<U> {
    return new ListLoaderPaginationBuilder<U>();
  }

  setLookupItemFunc(
    value: (offset: number, limit: number, sortColumn: ColumnSortModel<T>) => Observable<PaginationLookupResult<T>>
  ): ListLoaderPaginationBuilder<T> {
    this._lookupItemFunc = value;
    return this;
  }

  get lookupItemFunc(): (offset: number, limit: number, sortColumn: ColumnSortModel<T>) => Observable<PaginationLookupResult<T>> {
    return this._lookupItemFunc;
  }

  setPageSize(value: number): ListLoaderPaginationBuilder<T> {
    this._pageSize = value;
    return this;
  }

  get pageSize(): number {
    return this._pageSize;
  }

  setCurrentPage(value: number): ListLoaderPaginationBuilder<T> {
    this._currentPage = value;
    return this;
  }

  get currentPage(): number {
    return this._currentPage;
  }

  setNoSortModel(value: boolean): ListLoaderPaginationBuilder<T> {
    this._noSortModel = value;
    return this;
  }

  get noSortModel(): boolean {
    return this._noSortModel;
  }

  setThrowError(value: boolean) {
    this.throwError = value;
    return this;
  }

  isThrowError() {
    return this.throwError;
  }

  setTotalItems(value: number) {
    this._totalItems = value;
    return this;
  }

  get totalItems() {
    return this._totalItems;
  }

  build(): ListLoaderPagination<T> {
    return new ListLoaderPagination<T>(this);
  }
}
